import { TipCardConfig, TipCardSection, TipCardState } from '../types/tipcard';

export interface TipCardPlacement {
  tipId: string;
  config: TipCardConfig;
  section: TipCardSection;
  indexInSection: number; // Position within the section (0-based)
}

/**
 * Calculate where TipCards should be placed in the feed
 * Follows these rules:
 * 1. "first-in-section" tips appear immediately after section header (index 0)
 * 2. "listed-flow" tips flow across Para você → Outros pedidos sections
 * 3. First listed tip appears after 5 items minimum
 * 4. Minimum 5 items spacing between listed tips
 * 5. Express section is excluded from tip placement
 * 6. Each tip maintains its absolute position even when others are dismissed
 */
export function calculateTipCardPlacements(
  configs: TipCardConfig[],
  tipCardState: TipCardState,
  sectionItemCounts: {
    destaque: number;
    para_voce: number;
    outros: number;
  }
): {
  firstInSection: Record<TipCardSection, TipCardPlacement | null>;
  listedFlow: TipCardPlacement[];
} {
  const firstInSection: Record<TipCardSection, TipCardPlacement | null> = {
    destaque: null,
    para_voce: null,
    outros: null,
  };
  
  const listedFlow: TipCardPlacement[] = [];

  // Filter enabled tips (including dismissed ones for position calculation)
  const enabledTips = configs.filter((config) => config.enabled);

  // Process "first-in-section" tips (only show non-dismissed)
  const firstInSectionTips = enabledTips.filter(
    (config) => config.placementMode === 'first-in-section' && config.section && !tipCardState[config.id]?.dismissed
  );

  firstInSectionTips.forEach((config) => {
    const section = config.section as TipCardSection;
    // Only place in sections that have items (and never in Express)
    if (section !== 'destaque' && sectionItemCounts[section] > 0) {
      firstInSection[section] = {
        tipId: config.id,
        config,
        section,
        indexInSection: 0, // Always first item in section
      };
    } else if (section === 'destaque' && sectionItemCounts.destaque > 0) {
      firstInSection.destaque = {
        tipId: config.id,
        config,
        section: 'destaque',
        indexInSection: 0,
      };
    }
  });

  // Process "listed-flow" tips
  // Get ALL listed tips (dismissed and active) sorted by listOrder
  const allListedTips = enabledTips
    .filter((config) => config.placementMode === 'listed-flow')
    .sort((a, b) => (a.listOrder || 0) - (b.listOrder || 0));

  if (allListedTips.length === 0) {
    return { firstInSection, listedFlow };
  }

  // Calculate absolute positions for ALL tips (dismissed or not)
  // This ensures consistent positioning
  const sections: TipCardSection[] = ['para_voce', 'outros'];
  const tipPositions: Array<{
    tip: TipCardConfig;
    section: TipCardSection;
    indexInSection: number;
    continuousIndex: number;
  }> = [];

  let continuousItemIndex = 0;
  let tipSlotIndex = 0;

  sections.forEach((section) => {
    const itemCount = sectionItemCounts[section];
    
    for (let itemIndexInSection = 0; itemIndexInSection < itemCount; itemIndexInSection++) {
      continuousItemIndex++;

      // Calculate if a tip slot should exist at this position
      // First tip at position 5, then every 5 items after that
      const tipSlotPositions = [5, 10, 15, 20, 25, 30, 35, 40, 45, 50]; // Extend as needed
      
      if (tipSlotPositions.includes(continuousItemIndex) && tipSlotIndex < allListedTips.length) {
        tipPositions.push({
          tip: allListedTips[tipSlotIndex],
          section,
          indexInSection: itemIndexInSection,
          continuousIndex: continuousItemIndex,
        });
        tipSlotIndex++;
      }
    }
  });

  // Now filter to only include non-dismissed tips for actual rendering
  tipPositions.forEach((pos) => {
    if (!tipCardState[pos.tip.id]?.dismissed) {
      listedFlow.push({
        tipId: pos.tip.id,
        config: pos.tip,
        section: pos.section,
        indexInSection: pos.indexInSection,
      });
    }
  });

  return { firstInSection, listedFlow };
}

/**
 * Check if a TipCard should be rendered at a specific position
 */
export function shouldRenderTipAt(
  section: TipCardSection,
  itemIndex: number,
  placements: ReturnType<typeof calculateTipCardPlacements>
): TipCardPlacement | null {
  // Check first-in-section
  const firstInSectionTip = placements.firstInSection[section];
  if (firstInSectionTip && firstInSectionTip.indexInSection === itemIndex) {
    return firstInSectionTip;
  }

  // Check listed-flow
  const listedTip = placements.listedFlow.find(
    (placement) => 
      placement.section === section && 
      placement.indexInSection === itemIndex
  );
  
  return listedTip || null;
}
